import { useCallback, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useSearchParams } from "react-router-dom";

import { useCaseTemplateVersion } from "src/cases/queries/templates";
import {
  CaseTemplateFormValues,
  transformTemplateToFormValues,
} from "src/cases/templates/caseTemplateFormUtils";
import {
  FlattenedStatusValue,
  flattenStatusValues,
} from "src/cases/templates/utils";
import { isStatusProperty } from "src/cases/utils";

export const useCaseTemplateVersionForm = (
  templateId: string,
  versionId: string,
) => {
  return useCaseTemplateVersion<CaseTemplateFormValues>(
    { templateId, versionId },
    {
      select: transformTemplateToFormValues,
    },
  );
};

export const useTemplateVersionStatusValues = () => {
  const { getValues } = useFormContext<CaseTemplateFormValues>();
  const values = getValues("attributes.case_properties");

  const statusValues = useMemo(() => {
    const statusProperty = values?.find(isStatusProperty);
    return flattenStatusValues(statusProperty);
  }, [values]);

  /**
   * Get a status value by its status and optional sub-status.
   */
  const getStatusValue = useCallback(
    (statusKey: string) => {
      return statusValues.find((value) => statusKey === value._statusKey);
    },
    [statusValues],
  );

  return { statusValues, getStatusValue } as const;
};

export const useCurrentStatusValue = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const statusInternalId = searchParams.get("status");

  const setCurrentStatusValue = (statusValue: FlattenedStatusValue) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);

      newParams.set("status", statusValue._statusKey);

      return newParams;
    });
  };

  return [statusInternalId, setCurrentStatusValue] as const;
};

/**
 * Get case properties that can be used in automation rules (tags and enum types).
 */
export const useAutomationCaseProperties = () => {
  const properties = useWatch<
    CaseTemplateFormValues,
    "attributes.case_properties"
  >({ name: "attributes.case_properties" });

  return useMemo(
    () =>
      properties?.filter(
        (property) => property.type === "tags" || property.type === "enum",
      ),
    [properties],
  );
};
